import notifee from "@notifee/react-native"
import AsyncStorage from "@react-native-async-storage/async-storage"
import remoteConfig from "@react-native-firebase/remote-config"
import { PathConfigMap } from "@react-navigation/core"
import { useNavigation } from "@react-navigation/native"
import { createStackNavigator, StackNavigationOptions } from "@react-navigation/stack"
import {
  AlertProvider,
  HeaderBackButton,
  IUserContext,
  logger,
  useAlert,
  userContext,
  useTheme,
} from "capsule"
import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { Linking, Platform, StyleSheet, View } from "react-native"
import SplashScreen from "react-native-bootsplash"
import FastImage from "react-native-fast-image"
import { getLocales, getTimeZone } from "react-native-localize"

import { version } from "../../../package.json"
import { images } from "../../assets/images"
import RdvUrlView from "../../components/RdvUrl"
import ReviewUrlView from "../../components/ReviewUrlView"
import {
  CANCER_DATA_FORM,
  CHEMO_FORM,
  HORMONE_FORM,
  ICON_SIZE,
  IMMUNO_FORM,
  METASTATIC_FORM,
  ONCO_SURGERY_FORM,
  oneHourMillis,
  OTHER_PATH_FORM,
  RADIO_FORM,
} from "../../features/config/Constants"
import { updateUserData } from "../../features/models/UserDataFunctions"
import AboutScreen from "../../screens/AboutScreen/AboutScreen"
import ActivationCodeScreen from "../../screens/ActivationCodeScreen/ActivationCodeScreen"
import AddressScreen from "../../screens/AddressScreen/AddressScreen"
import ShareContactScreen from "../../screens/AddressScreen/ShareContact"
import AlertScreen from "../../screens/AlertScreen/AlertScreen"
import { alertFormikParams, AlertFormValues } from "../../screens/AlertScreen/Constants"
import CguScreen from "../../screens/CguScreen/CguScreen"
import CodeInfoScreen from "../../screens/CodeInfoScreen/CodeInfoScreen"
import CongratulationScreen from "../../screens/CongratulationScreen/CongratulationScreen"
import DeleteAccountScreen from "../../screens/DeleteAccountScreen/DeleteAccountScreen"
import { evaluationFormikParams } from "../../screens/EvaluationScreen/Constants"
import EvaluationScreen from "../../screens/EvaluationScreen/EvaluationScreen"
import ExerciseNavigator from "../../screens/ExerciseScreen/ExerciseNavigator"
import ExportDataScreen from "../../screens/ExportDataScreen/ExportDataScreen"
import KineSpecialtiesScreen from "../../screens/KineSpecialtiesScreen/KineSpecialtiesScreen"
import { LanguageSelector } from "../../screens/LanguageScreen/Constants"
import LanguageScreen from "../../screens/LanguageScreen/LanguageScreen"
import LinkPasswordProviderScreen from "../../screens/LinkPasswordProviderScreen/LinkPasswordProviderScreen"
import MigrationFinalizedScreen from "../../screens/LinkPasswordProviderScreen/MigrationFinalizedScreen"
import LoadingScreen from "../../screens/LoadingScreen/LoadingScreen"
import ForgotPasswordScreen from "../../screens/LoginScreen/ForgotPasswordScreen"
import LoginScreen from "../../screens/LoginScreen/LoginScreen"
import MfaLoginScreen from "../../screens/LoginScreen/MfaLoginScreen"
import MainNavigator, { screens as mainScreens } from "../../screens/MainScreen/MainNavigator"
import MedicalInfosScreen from "../../screens/MedicalInfosScreen/MedicalInfosScreen"
import MedicalTeamScreen from "../../screens/MedicalTeamScreen/MedicalTeamScreen"
import MfaRegisterScreen from "../../screens/MfaScreen/MfaRegisterScreen"
import MfaScreen from "../../screens/MfaScreen/MfaScreen"
import MfaValidationScreen from "../../screens/MfaScreen/MfaValidationScreen"
import NeedEmailVerifiedScreen from "../../screens/NeedEmailVerifiedScreen/NeedEmailVerifiedScreen"
import { NewClientNavigator } from "../../screens/NewClientOnboarding/NewClientNavigator"
import { oncoFormikParams, OncoFormValues } from "../../screens/OncoPathologyScreen/Constants"
import OncoPathologyNavigator, {
  screens as oncoPathologyScreens,
} from "../../screens/OncoPathologyScreen/OncoPathologyNavigator"
import OncoSettingsScreen from "../../screens/OncoSettingsScreen/OncoSettingsScreen"
import OncoSurgeriesScreen from "../../screens/OncoSurgeriesScreen/OncoSurgeriesScreen"
import { operationFormikParams, OperationFormValues } from "../../screens/OperationScreen/Constants"
import OperationNavigator, {
  screens as operationScreens,
} from "../../screens/OperationScreen/OperationNavigator"
import OperationScreen from "../../screens/OperationScreen/OperationScreen"
import OtherPathologiesScreen from "../../screens/OtherPathologiesScreen/OtherPathologiesScreen"
import ParametersEditionScreen from "../../screens/ParametersEditionScreen/ParametersEditionScreen"
import NeedNewPasswordScreen from "../../screens/PasswordModificationScreen/NeedNewPasswordScreen"
import PasswordModificationScreen from "../../screens/PasswordModificationScreen/PasswordModificationScreen"
import ValidationMfaPasswordModificationScreen from "../../screens/PasswordModificationScreen/ValidationMfaPasswordModificationScreen"
import PathologiesListScreen from "../../screens/PathologiesListScreen/PathologiesListScreen"
import PatientsRequestsScreen from "../../screens/PatientsRequestsScreen/PatientsRequestsScreen"
import PhoneScreen from "../../screens/PhoneScreen/PhoneScreen"
import PracticianListNavigator from "../../screens/PracticianListScreen/PracticianListNavigator"
import PracticianListScreen from "../../screens/PracticianListScreen/PracticianListScreen"
import PracticiansRequestedScreen from "../../screens/PracticiansRequestedScreen/PracticiansRequestedScreen"
import PractitionerProfileScreen from "../../screens/PractitionerProfileScreen/PractitionerProfileScreen"
import PractitionerScreen from "../../screens/PractitionerScreen/PractitionerScreen"
import { profileFormikParams, ProfileFormValues } from "../../screens/ProfileScreen/Constants"
import ProfileScreen from "../../screens/ProfileScreen/ProfileScreen"
import PromsNavigator, { screens as promsScreens } from "../../screens/PromsScreen/PromsNavigator"
import RegisterActivationCodeScreen from "../../screens/RegisterScreen/RegisterActivationCodeScreen"
import RegisterCguScreen from "../../screens/RegisterScreen/RegisterCguScreen"
import RegisterCodeAlreadyUsedScreen from "../../screens/RegisterScreen/RegisterCodeAlreadyUsedScreen"
import RegisterPasswordProviderScreen from "../../screens/RegisterScreen/RegisterPasswordProviderScreen"
import RegisterPecanWorkflowExplanation from "../../screens/RegisterScreen/RegisterPecanWorkflowExplanation"
import RegisterPracticianWorkflowScreen from "../../screens/RegisterScreen/RegisterPracticianWorkflowScreen"
import RegisterVerifyIdentityScreen from "../../screens/RegisterScreen/RegisterVerifyIdentityScreen"
import RegisterVerifyIdentitySuccessScreen from "../../screens/RegisterScreen/RegisterVerifyIdentitySuccessScreen"
import SmsValidationScreen from "../../screens/SMSValidationScreen/SMSValidationScreen"
import {
  surgeonPracticeFormikParams,
  SurgeonPracticeFormValues,
} from "../../screens/SurgeonPracticeScreen/Constants"
import SurgeonPracticeScreen from "../../screens/SurgeonPracticeScreen/SurgeonPracticeScreen"
import TherapiesScreen from "../../screens/TherapiesScreen/TherapiesScreen"
import VideoPresentationScreen from "../../screens/VideoPresentationScreen/VideoPresentationScreen"
import { maxWidth } from "../../ThemeApp"
import { renderFormattedText } from "../../utils/formatBoldTexts"
import { BOTTOM_SIZE } from "../config/Constants"
import useAutoLogout, { AUTO_LOGOUT_FLAG } from "../hooks/useAutoLogout"
import useDynalinks from "../hooks/useDynalinks"
import { useIsPasswordExpired } from "../hooks/useIsPasswordExpired"
import usePermissions from "../hooks/usePermissions"
import useSpecialtySystem from "../hooks/useSpecialtySystem"
import {
  ActivationCodeNS,
  AlertNS,
  LinkPasswordProviderNS,
  NavigationNS,
  NeedEmailVerifiedNS,
  OperationNS,
  PasswordModificationNS,
  PractitionerHomeNS,
  ProfileNS,
  SettingsNS,
  VideoNS,
} from "../i18n/constants"
import { removePreviousNotification } from "../models/AlertFunctions"
import { EvalFormValues, EvalType } from "../models/Evaluation"
import { AppUserData } from "../models/UserData"
import { useCode } from "../Providers/CodeProvider"
import { useMigrationAccount } from "../Providers/MigrationAccountProvider"
import { promsContext } from "../Providers/PromsProvider"
import SessionProvider from "../Providers/SessionProvider"
import { FormikParams, FormikScreen } from "./FormikScreen"
import MyWelcomeScreen from "./MyWelcomeScreen"
import { hideHeader } from "./NavigationService"
import { OnboardingParamList, RootParamList } from "./types"

export const screens: PathConfigMap<RootParamList> | PathConfigMap<OnboardingParamList> = {
  MainNavigator: { screens: mainScreens },
  Welcome: "welcome",
  Login: "login",
  ForgotPassword: "forgotPassword",
  Register: "register",
  RegisterActivationCode: "registerActivationCode",
  RegisterCgu: "registerCgu",
  RegisterResearchData: "registerResearchData",
  RegisterPecanWorkflowExplanation: "registerPecanWorkflowExplanation",
  RegisterVerifyIdentity: "registerVerifyIdentity",
  RegisterVerifyIdentitySuccess: "registerVerifyIdentitySuccess",
  RegisterPracticianWorkflow: "registerPracticianWorkflow",
  RegisterCodeAlreadyUsed: "registerCodeAlreadyUsed",
  LinkPasswordProvider: "linkPasswordProvider",
  NeedEmailVerified: "needEmailVerified",
  Mfa: "mfa",
  MfaRegister: "mfaRegister",
  MfaLogin: "mfaLogin",
  MfaValidation: "mfaValidation",
  MigrationFinalized: "migrationFinalized",
  Cgu: "cgu",
  DeleteAccount: "deleteAccount",
  ExportData: "exportData",
  ResearchData: "researchData",
  ActivationCode: "activation",
  Phone: "phone",
  SMSValidation: "sms",
  Specialty: "specialty",
  OperationNavigator: { screens: operationScreens, path: "surgery" },
  OncoPathologyNavigator: { screens: oncoPathologyScreens, path: "OncoPathology" },
  Profile: "profile",
  PasswordModification: "passwordModification",
  ValidationMfaPasswordModification: "validationMfaPasswordModification",
  NeedNewPassword: "needNewPassword",
  Alert: "alert",
  Practitioner: "pract",
  PractitionerProfile: "pract_profile",
  AddPatient: "add_patient",
  PractitionerProfileEdition: "profile_edit",
  PromsNavigator: { screens: promsScreens },
  ReviewUrl: "review_url",
  RdvUrl: "rdv_url",
  LoadingScreen: "loading",
}

export type OnboardingRouteNames = keyof OnboardingParamList
// noinspection JSUnusedGlobalSymbols
export type MainRouteNames = keyof RootParamList

export const onboardingNavigator = createStackNavigator<OnboardingParamList>()
export const rootNavigator = createStackNavigator<RootParamList>()

const minimumFetchInterval = 6 * oneHourMillis

const compareVersions = (currentVersion: string, newVersion: string) => {
  const currentParts = currentVersion.split(".").map(Number)
  const newParts = newVersion.split(".").map(Number)
  for (let i = 0; i < newParts.length; i++) {
    if (currentParts[i] < newParts[i]) {
      return true
    }
    if (currentParts[i] > newParts[i]) {
      return false
    }
  }
  return false
}

const RootNavigator: FC = () => {
  const { form, loadForm, setIsFromLink, isFromLink, loadSigninForm, loading } =
    useContext(promsContext)
  const [waitingInitialLink, setWaitingInitialLink] = useState(true)
  const [isDynalinkLoading, setIsDynalinkLoading] = useState(false)
  // The `useDynalinks` hook should always be called before any other actions in RootNavigator
  useDynalinks(loadForm, setIsFromLink, setWaitingInitialLink, setIsDynalinkLoading)
  const { user, userData, userDocRef, initializing, userDataLoading, logout } =
    useContext<IUserContext<AppUserData>>(userContext)
  const { showDialog, showSnack } = useAlert()
  const { createRequestNotification } = useSpecialtySystem()
  const isPaswordExpired = useIsPasswordExpired()
  const {
    colors: {
      black: { highEmphasis },
      surface: { background },
    },
    typography: { h6 },
  } = useTheme()

  const { code } = useCode()
  const { isMigrating } = useMigrationAccount()
  const isPatient = useMemo(() => userData?.role === "patient", [userData?.role])
  const isOnco = useMemo(() => userData?.specialty === "onco", [userData?.specialty])
  const isPecan = useMemo(() => userData?.partner === "PAR013", [userData?.partner])
  const isKine = useMemo(() => userData?.role === "kine", [userData?.role])
  const isOrthoOrImplant = useMemo(
    () => userData?.specialty === "ortho" || userData?.specialty === "implant",
    [userData?.specialty],
  )
  const supportedLanguages = LanguageSelector.map(lang => lang.languageCode)
  const deviceLanguage = getLocales()[0].languageCode
  const fallbackLanguage = "fr"
  const effectiveLanguage = supportedLanguages.includes(deviceLanguage)
    ? deviceLanguage
    : fallbackLanguage
  const appStoreLink = "https://apps.apple.com/app/id1574145501"
  const playStoreLink = "https://play.google.com/store/apps/details?id=com.siruplab.doctup"
  const storeLink = Platform.OS === "ios" ? appStoreLink : playStoreLink
  const { t } = useTranslation()
  const navigation = useNavigation<any>()
  const checkPermissions = isKine && Platform.OS !== "web" ? true : false
  usePermissions(checkPermissions)
  useAutoLogout(!!user && !!userData)

  useEffect(() => {
    if (initializing || user) {
      return
    }
    AsyncStorage.getItem(AUTO_LOGOUT_FLAG).then(value => {
      if (!value) {
        return
      }
      showDialog({
        type: "button",
        title: t(`${SettingsNS}:autoLogout.title`),
        message: t(`${SettingsNS}:autoLogout.message`),
        dismissable: false,
        positive: {
          label: t(`${SettingsNS}:autoLogout.confirm`),
          onPress: () => AsyncStorage.removeItem(AUTO_LOGOUT_FLAG),
        },
      })
    })
  }, [initializing, user, showDialog, t])

  useEffect(() => {
    if (
      userData &&
      userData.showRequestModal &&
      userData.showRequestModal === true &&
      userData?.patientsRequests &&
      Platform.OS !== "web"
    ) {
      const requestNumber = userData.patientsRequests.length
      createRequestNotification(requestNumber)
    }
  }, [createRequestNotification, userData, userData?.showRequestModal, userData?.patientsRequests])
  const checkForUpdates = useCallback(async () => {
    try {
      await remoteConfig().setDefaults({
        latest_version: version,
        force_update: false,
      })
      await remoteConfig().setConfigSettings({
        minimumFetchIntervalMillis: minimumFetchInterval,
      })

      const activated = await remoteConfig().fetchAndActivate()
      const latestVersion = remoteConfig().getString("latest_version")
      const forceUpdate = remoteConfig().getBoolean("force_update")
      const releaseInformation = remoteConfig().getString(
        userData?.lng
          ? `release_information_${userData?.lng}`
          : `release_information_${effectiveLanguage}`,
      )

      if (
        activated &&
        compareVersions(version, latestVersion) &&
        forceUpdate &&
        userData !== undefined &&
        userData.showUpdateModal !== false
      ) {
        showDialog({
          type: "button",
          title: t("common:upgrade.title"),
          message: renderFormattedText(releaseInformation),
          dismissable: false,
          positive: {
            onPress: async () => {
              Linking.openURL(storeLink)
              updateUserData(userDocRef, {
                showUpdateModal: false,
              })
            },
            label: t("common:upgrade.button"),
          },
        })
      } else if (
        latestVersion &&
        version === latestVersion &&
        userData !== undefined &&
        userData.showUpdateModal === false
      ) {
        updateUserData(userDocRef, {
          showUpdateModal: true,
        })
      }
    } catch (error) {
      logger("Error checking for updates:", error)
    }
  }, [userData, effectiveLanguage, showDialog, t, userDocRef, storeLink])

  useEffect(() => {
    if (user && !userDataLoading && userData !== undefined && Platform.OS !== "web") {
      checkForUpdates()
    }
  }, [user, userDataLoading, userData, checkForUpdates])
  useEffect(() => {
    const updateTimezoneIfNeeded = () => {
      const currentTimezone = getTimeZone()
      if (userData && (!userData.timezone || userData.timezone !== currentTimezone)) {
        updateUserData(userDocRef, {
          timezone: currentTimezone,
        })
      }
    }
    updateTimezoneIfNeeded()
    const intervalId = setInterval(() => {
      updateTimezoneIfNeeded()
    }, 60000)
    return () => clearInterval(intervalId)
  }, [userData, userDocRef])

  const rootOptions = useMemo<StackNavigationOptions>(
    () => ({
      gestureEnabled: false,
      headerBackTitleVisible: false,
      headerTitleStyle: { ...h6, color: highEmphasis },
      headerTintColor: highEmphasis,
      headerStyle: [styles.header, { backgroundColor: background }],
      headerTitleAlign: "left",
    }),
    [h6, highEmphasis, background],
  )
  const handleRequestModal = useCallback(
    (seeList?: boolean | undefined) => {
      if (seeList) {
        navigation.navigate("PatientsRequestsScreen")
      }
      updateUserData(userDocRef, {
        showRequestModal: false,
      })
    },
    [navigation, userDocRef],
  )

  useEffect(() => {
    if (isKine && userData && userData.showRequestModal === true && userData.patientsRequests) {
      const requestNumber = userData.patientsRequests.length
      const isMultipleRequests = requestNumber > 1
      showDialog({
        type: "button",
        title: t(
          isMultipleRequests
            ? `${PractitionerHomeNS}:validateRequestsModal.multipleTitle`
            : `${PractitionerHomeNS}:validateRequestsModal.title`,
        ),
        message: t(
          isMultipleRequests
            ? `${PractitionerHomeNS}:validateRequestsModal.multipleText`
            : `${PractitionerHomeNS}:validateRequestsModal.text`,
        ),
        dismissable: false,
        positive: {
          onPress: () => {
            handleRequestModal(true)
          },
          label: t(
            isMultipleRequests
              ? `${PractitionerHomeNS}:validateRequestsModal.multipleSeeList`
              : `${PractitionerHomeNS}:validateRequestsModal.seeList`,
          ),
          labelType: "cancel",
        },
        negative: {
          onPress: async () => {
            handleRequestModal()
          },
          label: t(`${PractitionerHomeNS}:validateRequestsModal.later`),
        },
      })
    }
  }, [user, userData, handleRequestModal, isKine, t, showDialog])
  const getSigninForm = useCallback(
    async (id: string) => {
      await loadSigninForm?.({ id })
    },
    [loadSigninForm],
  )
  useEffect(() => {
    if (!initializing && !userDataLoading && !waitingInitialLink && Platform.OS !== "web") {
      setTimeout(() => {
        SplashScreen.hide()
      }, 100)
    }
  }, [initializing, userDataLoading, waitingInitialLink])

  // Local notifications
  useEffect(() =>
    notifee.onBackgroundEvent(async ({ type, detail }) => {
      await removePreviousNotification(type, detail)
    }),
  )

  useEffect(
    () =>
      notifee.onForegroundEvent(async ({ type, detail }) => {
        await removePreviousNotification(type, detail)
      }),
    [],
  )
  const navOnboardingSwitcher = useMemo(() => {
    /** SIGN IN FLOW */
    if (form || isFromLink || loading) {
      return (
        <rootNavigator.Screen
          name="PromsNavigator"
          options={hideHeader}
          component={PromsNavigator}
        />
      )
    }

    if (userData?.disabled) {
      logout()
      showSnack({
        message: t(`${NavigationNS}:DeleteAccount:confirmationDeletion`),
      })
    }
    if (!user || (user && code)) {
      return (
        <>
          <onboardingNavigator.Screen
            name="Welcome"
            options={hideHeader}
            component={MyWelcomeScreen}
          />
          <rootNavigator.Screen
            name="Login"
            component={LoginScreen}
            options={{
              title: "",
            }}
          />
          <rootNavigator.Screen
            name="MfaLogin"
            component={MfaLoginScreen}
            options={{
              title: t(`${NavigationNS}:Login:smsCode`),
            }}
          />
          <rootNavigator.Screen
            name="ForgotPassword"
            component={ForgotPasswordScreen}
            options={{
              title: "",
            }}
          />
          <onboardingNavigator.Screen
            name="RegisterActivationCode"
            component={RegisterActivationCodeScreen}
            options={{
              title: t(""),
            }}
          />
          <onboardingNavigator.Screen
            name="NewClientNavigator"
            component={NewClientNavigator}
            options={hideHeader}
          />
          <onboardingNavigator.Screen
            name="RegisterCgu"
            options={{
              title: t(`${NavigationNS}:Register:cgu`),
            }}
          >
            {() => <RegisterCguScreen isCgu />}
          </onboardingNavigator.Screen>
          <onboardingNavigator.Screen
            name="RegisterResearchData"
            component={RegisterCguScreen}
            options={{ title: "" }}
          />
          <onboardingNavigator.Screen
            name="RegisterPecanWorkflowExplanation"
            component={RegisterPecanWorkflowExplanation}
            options={{
              title: "",
            }}
          />
          <onboardingNavigator.Screen
            name="RegisterPracticianWorkflow"
            component={RegisterPracticianWorkflowScreen}
            options={{
              title: t(`${NavigationNS}:Register:practicianWorkflow`),
            }}
          />
          <onboardingNavigator.Screen
            name="RegisterCodeAlreadyUsed"
            component={RegisterCodeAlreadyUsedScreen}
            options={{
              title: t(`${NavigationNS}:Register:codeAlreadyUsed`),
            }}
          />
          <onboardingNavigator.Screen
            name="Register"
            component={RegisterPasswordProviderScreen}
            options={{
              title: t(`${NavigationNS}:Register:title`),
            }}
          />
          <onboardingNavigator.Screen
            name="Cgu"
            options={{
              ...hideHeader,
            }}
          >
            {() => <CguScreen isCgu />}
          </onboardingNavigator.Screen>
          <onboardingNavigator.Screen
            name="ResearchData"
            component={CguScreen}
            options={{ title: "" }}
          />
          <onboardingNavigator.Screen
            name="Phone"
            component={PhoneScreen}
            options={{
              title: t(`${NavigationNS}:Phone:title`),
            }}
          />
          <onboardingNavigator.Screen
            name="SMSValidation"
            component={SmsValidationScreen}
            options={{
              title: t(`${NavigationNS}:SMSValidation:title`),
            }}
          />
          <onboardingNavigator.Screen
            name="Practitioner"
            options={{
              title: t(`${NavigationNS}:Practitioner:title`),
            }}
            component={PractitionerScreen}
          />
        </>
      )
    }
    if (user && !user.providerData.some(provider => provider.providerId === "password")) {
      return (
        <rootNavigator.Screen
          name="LinkPasswordProvider"
          component={LinkPasswordProviderScreen}
          options={{
            title: t(`${LinkPasswordProviderNS}:title`),
          }}
        />
      )
    }
    if (user && !user?.emailVerified) {
      return (
        <rootNavigator.Screen
          name="NeedEmailVerified"
          component={NeedEmailVerifiedScreen}
          options={{
            title: t(`${NeedEmailVerifiedNS}:title`),
          }}
        />
      )
    }
    if (user && user.multiFactor?.enrolledFactors.length === 0) {
      return (
        <>
          <rootNavigator.Screen
            name="Mfa"
            component={MfaScreen}
            options={{
              title: t(`${LinkPasswordProviderNS}:title`),
            }}
          />
          <rootNavigator.Screen
            name="MfaRegister"
            component={MfaRegisterScreen}
            options={{
              title: t(`${LinkPasswordProviderNS}:title`),
            }}
          />
          <rootNavigator.Screen
            name="MfaValidation"
            options={{
              title: t(`${NavigationNS}:SMSValidation:title`),
            }}
          >
            {props => <MfaValidationScreen {...props} />}
          </rootNavigator.Screen>
        </>
      )
    }
    if (user && isMigrating) {
      return (
        <rootNavigator.Screen
          name="MigrationFinalized"
          options={{
            title: t(`${NavigationNS}:MigrationFinalized:title`),
          }}
          component={MigrationFinalizedScreen}
        />
      )
    }

    if ((isPatient && !userData?.code) || (!isPatient && !userData?.firstActivation)) {
      return (
        <onboardingNavigator.Screen
          name="ActivationCode"
          component={ActivationCodeScreen}
          options={{
            title: t(`${ActivationCodeNS}:title`),
          }}
        />
      )
    }
    if (isPaswordExpired) {
      return (
        <>
          <rootNavigator.Screen
            name="NeedNewPassword"
            options={{
              title: t(""),
              headerLeft: props => (
                <HeaderBackButton
                  {...props}
                  onPress={() => {
                    logout()
                  }}
                  vectorIcon={{
                    name: "close",
                    category: "MaterialIcons",
                    size: ICON_SIZE,
                    color: highEmphasis,
                  }}
                  labelVisible={false}
                />
              ),
            }}
            component={NeedNewPasswordScreen}
          />
          <rootNavigator.Screen
            name="PasswordModification"
            options={{
              title: t(`${PasswordModificationNS}:title`),
            }}
            component={PasswordModificationScreen}
          />
          <rootNavigator.Screen
            name="ValidationMfaPasswordModification"
            options={{
              title: t(`${PasswordModificationNS}:title`),
            }}
            component={ValidationMfaPasswordModificationScreen}
          />
        </>
      )
    }

    if (isPatient) {
      if (!userData?.pathology) {
        return (
          <>
            {isOnco ? (
              <onboardingNavigator.Screen name="OncoPathologyNavigator" options={hideHeader}>
                {props => (
                  <FormikScreen<OncoFormValues>
                    {...props}
                    component={OncoPathologyNavigator}
                    formikParams={oncoFormikParams(t, OperationNS)}
                  />
                )}
              </onboardingNavigator.Screen>
            ) : (
              <onboardingNavigator.Screen name="OperationNavigator" options={hideHeader}>
                {props => (
                  <FormikScreen<OperationFormValues>
                    {...props}
                    component={OperationNavigator}
                    formikParams={operationFormikParams(t, OperationNS)}
                  />
                )}
              </onboardingNavigator.Screen>
            )}
          </>
        )
      }
      if (!userData?.lastName || (isPecan && userData.profileNeedAnUpdate)) {
        return (
          <onboardingNavigator.Screen
            name="Profile"
            options={{
              title: t(`${NavigationNS}:Profile:title`),
            }}
          >
            {props => (
              <FormikScreen<ProfileFormValues>
                {...props}
                isEdit={isPecan && userData.profileNeedAnUpdate}
                component={ProfileScreen}
                formikParams={profileFormikParams(
                  t,
                  ProfileNS,
                  isPecan && userData.profileNeedAnUpdate ? userData : undefined,
                )}
              />
            )}
          </onboardingNavigator.Screen>
        )
      }

      // Vérification FranceConnect+ pour PECAN
      if (isPecan && userData?.lastName && !userData?.fcSub) {
        return (
          <onboardingNavigator.Screen
            name="RegisterVerifyIdentity"
            options={{
              title: t(`${NavigationNS}:RegisterVerifyIdentity:title`),
            }}
            component={RegisterVerifyIdentityScreen}
          />
        )
      }
      if (isPecan && userData?.lastName && userData?.fcSub && !userData?.isFranceConnectValidated) {
        return (
          <onboardingNavigator.Screen
            name="RegisterVerifyIdentitySuccess"
            component={RegisterVerifyIdentitySuccessScreen}
            options={{
              title: t(`${NavigationNS}:RegisterVerifyIdentitySuccess:title`),
            }}
          />
        )
      }
      const medicalInfo = userData?.medicalInfo
      if (isOnco) {
        if (!medicalInfo?.cancerData) {
          getSigninForm(CANCER_DATA_FORM)
        } else if (!medicalInfo?.surgery) {
          getSigninForm(ONCO_SURGERY_FORM)
        } else if (!medicalInfo?.chemoTh) {
          getSigninForm(CHEMO_FORM)
        } else if (!medicalInfo?.radioTh) {
          getSigninForm(RADIO_FORM)
        } else if (!medicalInfo?.hormoneTh) {
          getSigninForm(HORMONE_FORM)
        } else if (!medicalInfo?.immunoTh) {
          getSigninForm(IMMUNO_FORM)
        } else if (!medicalInfo?.metastatic) {
          getSigninForm(METASTATIC_FORM)
        } else if (!medicalInfo?.otherPathologies) {
          getSigninForm(OTHER_PATH_FORM)
        }
      }

      if (
        (isOrthoOrImplant && !userData?.alertDays) ||
        (isOnco && medicalInfo?.otherPathologies && !userData?.alertDays)
      ) {
        return (
          <onboardingNavigator.Screen
            name="Alert"
            options={{
              title: t(`${NavigationNS}:Alert:title`),
            }}
          >
            {props => (
              <FormikScreen<AlertFormValues>
                {...props}
                component={AlertScreen}
                formikParams={{
                  validateOnMount: true,
                  ...alertFormikParams(t, AlertNS, userData?.specialty),
                }}
              />
            )}
          </onboardingNavigator.Screen>
        )
      }
      if (!userData?.videoWatched) {
        return (
          <onboardingNavigator.Screen
            name="Video"
            component={VideoPresentationScreen}
            options={{
              title: t(`${VideoNS}:title`),
            }}
          />
        )
      }
    }

    if (!isPatient) {
      if (
        !userData?.lastName ||
        !userData?.email ||
        (userData?.role === "surgeon" && !userData?.rppsNumber)
      ) {
        return (
          <onboardingNavigator.Screen
            name="PractitionerProfile"
            options={{
              title: t(`${NavigationNS}:Profile:title`),
            }}
          >
            {props => (
              // @ts-ignore
              <FormikScreen<ProfileFormValues>
                {...props}
                component={PractitionerProfileScreen}
                formikParams={profileFormikParams(t, ProfileNS)}
              />
            )}
          </onboardingNavigator.Screen>
        )
      }

      if (!userData?.surgeonPractice && userData?.role === "surgeon") {
        return (
          <onboardingNavigator.Screen
            name="SurgeonPractice"
            options={{
              title: t(`${NavigationNS}:SurgeonPractice:title`),
            }}
          >
            {props => (
              // @ts-ignore
              <FormikScreen<SurgeonPracticeFormValues>
                {...props}
                component={SurgeonPracticeScreen}
                formikParams={surgeonPracticeFormikParams(t, ProfileNS, userData)}
              />
            )}
          </onboardingNavigator.Screen>
        )
      }
      if (userData?.role === "kine") {
        if (userData?.shareContact === undefined) {
          return (
            <onboardingNavigator.Screen
              name="ShareContact"
              options={{
                title: t(`${NavigationNS}:ShareContact:title`),
              }}
              component={ShareContactScreen}
            />
          )
        }
        if (userData?.shareContact === true && !userData?.address) {
          return (
            <onboardingNavigator.Screen
              name="Address"
              component={AddressScreen}
              options={{
                title: t(`${NavigationNS}:Address:title`),
              }}
            />
          )
        }
        if (userData?.address && !userData.kineSpecialties) {
          return (
            <onboardingNavigator.Screen
              options={{
                title: t(`${NavigationNS}:KineSpecialties.title`),
              }}
              name="KineSpecialties"
              component={KineSpecialtiesScreen}
            />
          )
        }
        if (userData?.address && !userData.rdvLink && userData.rdvLink !== "") {
          return (
            <onboardingNavigator.Screen
              options={{
                title: t(`${NavigationNS}:RdvUrl.title`),
              }}
              name="RdvUrl"
              component={RdvUrlView}
            />
          )
        }
      }
    }

    return null
  }, [
    form,
    isFromLink,
    loading,
    userData,
    user,
    code,
    isMigrating,
    isPatient,
    isPaswordExpired,
    logout,
    showSnack,
    t,
    highEmphasis,
    isOnco,
    isOrthoOrImplant,
    getSigninForm,
  ])
  const navSwitcher = useMemo(
    () =>
      !user ||
      (isPatient && (!userData?.code || !userData?.pathology || !userData.lastName)) ? null : (
        <>
          {/** USER FLOW */}
          {Platform.OS === "web" && isFromLink ? (
            <rootNavigator.Screen
              name="PromsNavigator"
              options={hideHeader}
              component={PromsNavigator}
            />
          ) : null}
          <rootNavigator.Screen name="MainNavigator" options={hideHeader}>
            {() => (
              <AlertProvider hasBottomNav bottomHeight={BOTTOM_SIZE}>
                <MainNavigator />
              </AlertProvider>
            )}
          </rootNavigator.Screen>

          <rootNavigator.Screen options={hideHeader} name="ExerciseNavigator">
            {() => (
              <SessionProvider>
                <ExerciseNavigator />
              </SessionProvider>
            )}
          </rootNavigator.Screen>
          <rootNavigator.Screen name="EndOfFirstSessionEvaluation" options={hideHeader}>
            {props => (
              <FormikScreen<EvalFormValues>
                {...props}
                screenParams={{ evalType: EvalType.EVENING }}
                component={EvaluationScreen}
                formikParams={
                  evaluationFormikParams(
                    EvalType.EVENING,
                    userData?.specialty,
                  ) as FormikParams<EvalFormValues>
                }
              />
            )}
          </rootNavigator.Screen>
          <rootNavigator.Screen name="MorningEvaluation" options={hideHeader}>
            {props => (
              <FormikScreen<EvalFormValues>
                {...props}
                screenParams={{ evalType: EvalType.MORNING }}
                component={EvaluationScreen}
                formikParams={
                  evaluationFormikParams(
                    EvalType.MORNING,
                    userData?.specialty,
                  ) as FormikParams<EvalFormValues>
                }
              />
            )}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            name="Congratulation"
            options={hideHeader}
            component={CongratulationScreen}
          />
          <rootNavigator.Screen
            name="AddPatient"
            options={{
              title: t(`${ActivationCodeNS}:title`),
            }}
          >
            {() => <ActivationCodeScreen addPatient />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            name="PatientsRequestsScreen"
            options={{
              title: t("common:PatientsRequests"),
            }}
            component={PatientsRequestsScreen}
          />
          {/** EDITION FLOW */}
          <rootNavigator.Screen
            name="PhoneEdition"
            options={{
              title: t(`${NavigationNS}:Phone:title`),
            }}
          >
            {() => <PhoneScreen isEdit />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            name="LanguageEdition"
            options={{
              title: t(`${NavigationNS}:Language:title`),
            }}
          >
            {() => <LanguageScreen isEdit />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            name="SMSValidationEdition"
            options={{
              title: t(`${NavigationNS}:SMSValidation:title`),
            }}
          >
            {props => <SmsValidationScreen isEdit {...props} />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            name="MedicalInfos"
            options={{
              title: t(`${NavigationNS}:MedicalInfos:title`),
            }}
          >
            {() => <MedicalInfosScreen />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            name="OperationEdition"
            options={{
              title: t(`${NavigationNS}:Intervention:title`),
            }}
          >
            {props => (
              <FormikScreen<OperationFormValues>
                {...props}
                component={OperationScreen}
                isEdit
                formikParams={operationFormikParams(t, OperationNS, userData)}
              />
            )}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            name="MedicalTeamEdition"
            options={{
              title: t(`${NavigationNS}:MedicalTeam:title`),
            }}
          >
            {() => <MedicalTeamScreen />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            name="KineList"
            component={PracticianListNavigator}
            options={{
              title: t(`${NavigationNS}:PracticianList:kine`),
            }}
          />
          <rootNavigator.Screen
            name="CodeInfoScreen"
            options={{
              title: t(`${NavigationNS}:CodeInfo:title`),
            }}
          >
            {() => <CodeInfoScreen />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            name="KineInfoScreen"
            options={{
              title: t(`${NavigationNS}:CodeInfo:title`),
            }}
          >
            {() => <CodeInfoScreen isKine />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            name="KineRequestedScreen"
            options={{
              title: t(`${NavigationNS}:PracticiansRequestedScreen:title`),
            }}
          >
            {() => <PracticiansRequestedScreen role="kine" />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            name="SurgeonList"
            options={{
              title: t(`${NavigationNS}:PracticianList:surgeon`),
            }}
          >
            {() => <PracticianListScreen role="surgeon" />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            options={{
              title: t(`${NavigationNS}:Operation:title`),
            }}
            name="OncoDataEdition"
          >
            {() => <OncoSettingsScreen />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            options={{
              title: t(`${NavigationNS}:Operation:surgeries_title`),
            }}
            name="OncoSurgeries"
          >
            {() => <OncoSurgeriesScreen />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            options={{
              title: t(`${NavigationNS}:Operation:therapies`),
            }}
            name="Therapies"
          >
            {() => <TherapiesScreen />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            options={{
              title: t(`${NavigationNS}:Operation:other_pathologies`),
            }}
            name="OtherPathologies"
          >
            {() => <OtherPathologiesScreen />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            options={{
              title: t(`${NavigationNS}:Operation:heart_disease`),
            }}
            name="HeartDisease"
          >
            {() => <PathologiesListScreen isHeartDisease />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            options={{
              title: t(`${NavigationNS}:Operation:respiratory_disease`),
            }}
            name="RespiratoryDisease"
          >
            {() => <PathologiesListScreen />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            name="ProfileEdition"
            options={{
              title: t(`${NavigationNS}:Profile:title`),
            }}
          >
            {props => (
              <FormikScreen<ProfileFormValues>
                {...props}
                isEdit
                component={ProfileScreen}
                formikParams={profileFormikParams(t, ProfileNS, userData)}
              />
            )}
          </rootNavigator.Screen>

          <rootNavigator.Screen
            name="PasswordModification"
            options={{
              title: t(`${NavigationNS}:PasswordModification:title`),
            }}
            component={PasswordModificationScreen}
          />

          <rootNavigator.Screen
            name="ForgotPassword"
            component={ForgotPasswordScreen}
            options={{
              title: "",
            }}
          />
          <rootNavigator.Screen
            name="ValidationMfaPasswordModification"
            options={{
              title: t(`${NavigationNS}:PasswordModification:title`),
            }}
            component={ValidationMfaPasswordModificationScreen}
          />

          <rootNavigator.Screen
            name="AlertEdition"
            options={{
              title: t(`${NavigationNS}:Alert:title`),
            }}
          >
            {props => (
              <FormikScreen<AlertFormValues>
                {...props}
                isEdit
                component={AlertScreen}
                formikParams={alertFormikParams(t, AlertNS, userData?.specialty, userData)}
              />
            )}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            options={{
              title: t(`${NavigationNS}:ReviewUrl.title`),
            }}
            name="ReviewUrl"
            component={ReviewUrlView}
          />
          <rootNavigator.Screen
            options={{
              title: t(`${NavigationNS}:AboutScreen.title`),
            }}
            name="AboutScreen"
            component={AboutScreen}
          />
          <rootNavigator.Screen
            options={{
              title: t(`${NavigationNS}:Parameters.title`),
            }}
            name="ParametersEdition"
            component={ParametersEditionScreen}
          />
          <rootNavigator.Screen
            name="SurgeonPracticeEdition"
            options={{
              title: t(`${NavigationNS}:SurgeonPractice.title`),
            }}
          >
            {props => (
              <FormikScreen<SurgeonPracticeFormValues>
                {...props}
                isEdit
                component={SurgeonPracticeScreen}
                formikParams={surgeonPracticeFormikParams(t, ProfileNS, userData)}
              />
            )}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            name="PractitionerProfileEdition"
            options={{
              title: t(`${NavigationNS}:Profile.title`),
            }}
          >
            {props => <PractitionerProfileScreen {...props} isEdit />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            options={hideHeader}
            name="PromsNavigator"
            component={PromsNavigator}
          />
          <rootNavigator.Screen
            options={{
              title: t(`${NavigationNS}:Research:title`),
            }}
            name="Research"
          >
            {() => <CguScreen isAccount />}
          </rootNavigator.Screen>
          <rootNavigator.Screen
            options={{
              title: t(`${NavigationNS}:DeleteAccount:title`),
            }}
            name="DeleteAccount"
            component={DeleteAccountScreen}
          />
          <rootNavigator.Screen
            options={{
              title: t(`${SettingsNS}:exportData`),
            }}
            name="ExportData"
            component={ExportDataScreen}
          />
        </>
      ),
    [isPatient, isFromLink, t, user, userData],
  )

  return initializing || userDataLoading ? null : (
    <rootNavigator.Navigator initialRouteName="Onboarding" screenOptions={rootOptions}>
      {isDynalinkLoading ? (
        <rootNavigator.Screen options={hideHeader} name="LoadingScreen" component={LoadingScreen} />
      ) : navOnboardingSwitcher ? (
        <rootNavigator.Screen name="Onboarding" options={hideHeader}>
          {() => <Onboarding screenOptions={rootOptions}>{navOnboardingSwitcher}</Onboarding>}
        </rootNavigator.Screen>
      ) : (
        navSwitcher
      )}
    </rootNavigator.Navigator>
  )
}

const Onboarding: FC<PropsWithChildren<{ screenOptions: StackNavigationOptions }>> = ({
  children,
  screenOptions,
}) => (
  <View style={styles.container}>
    {Platform.OS === "web" ? (
      <>
        <View style={{ width: maxWidth }}>
          <onboardingNavigator.Navigator screenOptions={screenOptions}>
            {children}
          </onboardingNavigator.Navigator>
        </View>
        <FastImage
          source={images.right_placeholder as number}
          style={styles.image}
          resizeMode="cover"
        />
      </>
    ) : (
      <onboardingNavigator.Navigator screenOptions={screenOptions}>
        {children}
      </onboardingNavigator.Navigator>
    )}
  </View>
)

const styles = StyleSheet.create({
  header: {
    elevation: 0,
    shadowOpacity: 0,
    borderBottomWidth: 0,
  },
  container: { flexDirection: "row", width: "100%", height: "100%" },
  image: { flex: 1, height: "100%" },
})

export default RootNavigator
